import React from 'react';
import { cn } from '@/lib/utils';
import { useWillStore } from '@/lib/store';
import { isEstateSectionComplete } from '@/lib/will-logic';
import { Check, Clock } from 'lucide-react';

// Visible steps only (Specific Gifts intentionally omitted)
const steps = [
  { id: 0, name: 'About you', shortName: 'About', estimateMinutes: 2 },
  { id: 1, name: 'Guardians', shortName: 'Guardians', estimateMinutes: 2 },
  { id: 2, name: 'My Estate', shortName: 'Estate', estimateMinutes: 3 },
  { id: 3, name: 'Executors', shortName: 'Executors', estimateMinutes: 2 },
  { id: 5, name: 'Summary', shortName: 'Summary', estimateMinutes: 2 },
];

export const StepIndicator = () => {
  const { currentStep, setStep, data, completedSteps } = useWillStore();
  const activeSteps = steps;
  const totalSteps = activeSteps.length;

  const getVisibleIndex = (stepId: number) => {
    const exactIndex = activeSteps.findIndex((s) => s.id === stepId);
    if (exactIndex !== -1) return exactIndex;

    // For hidden steps (e.g., Specific Gifts), map to the previous visible step
    const priorVisible = [...activeSteps].reverse().find((s) => s.id < stepId);
    if (priorVisible) return activeSteps.findIndex((s) => s.id === priorVisible.id);
    return 0;
  };

  const currentVisibleIndex = getVisibleIndex(currentStep);
  const displayStep = currentVisibleIndex + 1;

  // Check actual data completion for each step
  const isStepComplete = (stepId: number): boolean => {
    switch (stepId) {
      case 0: // About You - complete if explicitly marked complete (after assets page)
        return completedSteps.includes(0);
      case 1: // Guardians - complete if children AND pets questions have been properly answered
        // Must have explicitly answered both questions (not undefined)
        const hasChildrenAnswered = data.hasChildren === true || data.hasChildren === false;
        const hasPetsAnswered = data.hasPets === true || data.hasPets === false;
        // If they said yes to children, must have added at least one child
        const childrenValid = data.hasChildren === false || (data.children?.length ?? 0) > 0;
        // If they said yes to pets, must have added at least one pet
        const petsValid = data.hasPets === false || (data.pets?.length ?? 0) > 0;
        return hasChildrenAnswered && hasPetsAnswered && childrenValid && petsValid;
      case 2: // Your Estate - complete only after finishing ALL estate sub-steps (including exclusions)
        // Check if they've explicitly completed the estate section (submitted from exclusions page)
        return isEstateSectionComplete(data);
      case 3: // Executors - complete if executors added
        return (data.executors?.length ?? 0) > 0;
      case 5: // Summary/Review - complete only after payment/submission
        return data.status === 'paid' || data.status === 'submitted';
      default:
        return currentStep > stepId;
    }
  };
  const completedCoreSteps = [0, 1, 2, 3].filter((id) => isStepComplete(id)).length;
  const remainingMinutes = activeSteps.reduce((minutes, step) => {
    return isStepComplete(step.id) ? minutes : minutes + (step.estimateMinutes || 1);
  }, 0);
  const progressPercent = ((displayStep) / totalSteps) * 100;
  const timeLabel = remainingMinutes <= 0
    ? 'Complete'
    : remainingMinutes === 1
      ? '~1 min left'
      : `~${remainingMinutes} min left`;

  return (
    <nav className="w-full pt-2 pb-0 font-display" aria-label="Progress">
      <div className="space-y-4">
        <div className="flex flex-wrap items-center justify-between gap-2 text-sm md:text-base">
          <div className="flex flex-wrap items-center gap-2">
            <span className="font-semibold text-primary">
              Step {displayStep} of {totalSteps}
            </span>
            <span className="text-gray-600">· {activeSteps[currentVisibleIndex]?.name}</span>
          </div>
          <div className="flex items-center gap-2 text-sm text-gray-600" aria-live="polite">
            <Clock className="h-4 w-4 text-gray-400" aria-hidden="true" />
            <span>{timeLabel}</span>
          </div>
        </div>

        <div className="hidden md:grid md:grid-cols-5 lg:grid-cols-5 gap-3 text-sm pt-2 w-full">
          {activeSteps.map((step, index) => {
            const isCompleted = isStepComplete(step.id);
            const isCurrent = currentVisibleIndex === index;

            return (
              <button
                key={step.id}
                type="button"
                onClick={() => setStep(step.id)}
                className={cn(
                  'flex items-center justify-center gap-3 px-4 py-3 rounded-xl transition-all w-full group border shadow-sm',
                  'hover:border-primary/30 hover:shadow-md cursor-pointer',
                  isCurrent
                    ? 'bg-white border-primary ring-1 ring-primary/10 shadow-md'
                    : isCompleted
                      ? 'bg-teal/5 border-teal/30 text-teal'
                      : 'bg-white border-gray-200',
                  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40'
                )}
              >
                {isCompleted ? (
                  <div className="w-6 h-6 rounded-full bg-teal text-white flex items-center justify-center shadow-sm group-hover:scale-110 transition-transform">
                    <Check className="h-3.5 w-3.5" />
                  </div>
                ) : (
                  <div className={cn("w-6 h-6 rounded-full border-2 flex items-center justify-center text-[11px] font-bold transition-colors", isCurrent ? "border-primary text-primary" : "border-gray-200 text-gray-300 group-hover:border-gray-300 group-hover:text-gray-400")}>
                    {index + 1}
                  </div>
                )}
                <span className={cn(
                  "text-sm font-medium transition-colors",
                  isCurrent
                    ? "text-gray-900"
                    : isCompleted
                      ? "text-teal"
                      : "text-gray-500 group-hover:text-gray-700"
                )}>{step.name}</span>
              </button>
            );
          })}
        </div>

        {/* Mobile: compact grid */}
        <div className="md:hidden grid grid-cols-2 gap-2 w-full">
          {activeSteps.map((step, index) => {
            const isCompleted = isStepComplete(step.id);
            const isCurrent = currentVisibleIndex === index;

            return (
              <button
                key={step.id}
                type="button"
                onClick={() => setStep(step.id)}
                className={cn(
                  'flex items-center gap-2 border rounded-lg px-3 py-2 text-sm font-medium transition-all',
                  'bg-white border-gray-200 hover:border-primary/50 cursor-pointer shadow-sm',
                  isCurrent && 'border-primary bg-primary/5 text-primary ring-1 ring-primary/20',
                  isCompleted && !isCurrent && 'border-teal/30 text-teal bg-teal/5',
                  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40'
                )}
              >
                {isCompleted ? (
                  <Check className="h-3.5 w-3.5" />
                ) : (
                  <span className="text-[10px] opacity-60">{index + 1}</span>
                )}
                <span className="truncate">{step.shortName}</span>
              </button>
            );
          })}
        </div>
      </div>
    </nav>
  );
};
